import React, { useState } from "react";
import { Check, Clock, RotateCcw, Star } from "lucide-react";

const SIZES = ["Small", "Medium", "Large"];
const VARIANTS = ["Option A", "Option B"];
const MODIFIERS = ["Extra sauce", "No onions", "Add cheese"];

/** Interactive mock of the item customisation sheet (kept from the previous page). */
export function EatCustomisation() {
  const [size, setSize] = useState("Medium");
  const [variant, setVariant] = useState("Option A");
  const [modifiers, setModifiers] = useState<string[]>(["Extra sauce"]);

  const toggleModifier = (modifier: string) =>
    setModifiers(prev => (prev.includes(modifier) ? prev.filter(m => m !== modifier) : [...prev, modifier]));

  return (
    <section className="bg-white py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">Customisation</p>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
            Make It Exactly How You Want It
          </h2>
          <p className="pt-[16px] text-[18px] leading-[29.25px] text-[#717182]">
            Choose sizes, swap variants and add modifiers to get your meal exactly right — before it's made.
          </p>

          <div className="mt-8 rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] p-6">
            <div className="flex items-center gap-[16px]">
              <img src="/figma-assets/eat/menu-item.jpg" alt="" className="size-16 rounded-[14px] object-cover" />
              <div>
                <p className="text-[16px] font-bold leading-6 text-[#0a0a0a]">Menu Item</p>
                <p className="text-[14px] leading-5 text-[#717182]">Your restaurant</p>
              </div>
            </div>

            <p className="pt-[20px] text-[14px] font-semibold leading-5 text-[#0a0a0a]">Size</p>
            <div className="flex gap-2 pt-2.5">
              {SIZES.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setSize(option)}
                  className={`rounded-[14px] px-[16px] py-2 text-[12px] font-semibold leading-4 transition-colors ${
                    size === option
                      ? "bg-[#017bef] text-white drop-shadow-[0_2px_6px_rgba(1,123,239,0.25)]"
                      : "border-[1px] border-solid border-[#e5e7eb] bg-white text-[#717182] hover:text-[#0a0a0a]"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>

            <p className="pt-[20px] text-[14px] font-semibold leading-5 text-[#0a0a0a]">Variant</p>
            <div className="grid grid-cols-2 gap-2 pt-2.5">
              {VARIANTS.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setVariant(option)}
                  className={`rounded-[14px] border-[1px] border-solid px-[16px] py-2.5 text-[12px] font-semibold leading-4 transition-colors ${
                    variant === option
                      ? "border-[#017bef] bg-[#017bef]/5 text-[#017bef]"
                      : "border-[#e5e7eb] text-[#717182] hover:text-[#0a0a0a]"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>

            <p className="pt-[20px] text-[14px] font-semibold leading-5 text-[#0a0a0a]">Modifiers</p>
            <div className="space-y-2 pb-[20px] pt-2.5">
              {MODIFIERS.map(modifier => {
                const selected = modifiers.includes(modifier);
                return (
                  <button
                    key={modifier}
                    type="button"
                    onClick={() => toggleModifier(modifier)}
                    aria-pressed={selected}
                    className="flex w-full items-center justify-between rounded-[14px] border-[1px] border-solid border-[#e5e7eb] bg-white px-[16px] py-2.5 text-left"
                  >
                    <span className="text-[12px] leading-4 text-[#717182]">{modifier}</span>
                    <span
                      className={`flex size-4 items-center justify-center rounded-full border-2 border-solid ${
                        selected ? "border-[#017bef] bg-[#017bef]" : "border-[#e5e7eb]"
                      }`}
                    >
                      {selected && <Check className="size-[9px] text-white" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Illustration of the app, so this is not a real action */}
            <p
              aria-hidden
              className="w-full rounded-2xl bg-[#017bef] py-[14px] text-center text-[14px] font-bold leading-5 text-white drop-shadow-[0_2px_8px_rgba(1,123,239,0.2)]"
            >
              Add to Cart
            </p>
          </div>
        </div>

        <div className="relative h-[360px] overflow-hidden rounded-3xl sm:h-[492px]">
          <img
            src="/figma-assets/eat/kitchen.jpg"
            alt="Kitchen preparing an order"
            className="absolute inset-0 size-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
        </div>
      </div>
    </section>
  );
}

const cardHeading = (eyebrow: string, title: string, description: string) => (
  <>
    <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">{eyebrow}</p>
    <h2 className="pt-[12px] text-[26px] font-bold leading-9 tracking-[-0.75px] text-[#0a0a0a] sm:text-[30px]">
      {title}
    </h2>
    <p className="pt-[16px] text-[16px] leading-[26px] text-[#717182]">{description}</p>
  </>
);

export function EatCartAndFavourites() {
  return (
    <section className="bg-[#f8fbff] py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-12 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          {cardHeading(
            "Persistent Cart",
            "Pick Up Where You Left Off",
            "Your cart stays saved. Come back to it whenever you're ready."
          )}
          <div className="mt-8 rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white p-6">
            <div className="flex items-center justify-between">
              <span className="text-[14px] font-bold leading-5 text-[#0a0a0a]">Your Cart</span>
              <span className="text-[12px] leading-4 text-[#717182]">Saved</span>
            </div>
            <div className="space-y-[16px] pb-[16px] pt-2">
              {["Item one", "Item two", "Item three"].map((item, index) => (
                <div key={item} className="flex items-center gap-[12px] rounded-[14px] bg-[#f8fbff] p-[12px]">
                  <span className="size-10 shrink-0 rounded-[10px] bg-[#e5e7eb]" />
                  <div className="flex-1">
                    <p className="text-[14px] font-semibold leading-5 text-[#0a0a0a]">{item}</p>
                    <p className="text-[12px] leading-4 text-[#717182]">Qty {index + 1}</p>
                  </div>
                  <span className="size-2 rounded-full bg-[#017bef]/25" />
                </div>
              ))}
            </div>
            <p
              aria-hidden
              className="w-full rounded-2xl bg-[#017bef] py-[14px] text-center text-[14px] font-bold leading-5 text-white"
            >
              Continue to Checkout
            </p>
          </div>
        </div>

        <div>
          {cardHeading(
            "Saved Favourites",
            "Your Favourites, One Tap Away",
            "Rediscover and reorder your favourite meals in a tap."
          )}
          <div className="mt-8 rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white p-6">
            <div className="flex items-center justify-between">
              <span className="text-[14px] font-bold leading-5 text-[#0a0a0a]">Saved Favourites</span>
              <Star className="size-3.5 text-[#017bef]" />
            </div>
            <div className="space-y-[16px] pt-2">
              {["one", "two", "three"].map((name, index) => (
                <div key={name} className="flex items-center gap-[12px] rounded-[14px] bg-[#f8fbff] p-[12px]">
                  <img
                    src={`/figma-assets/eat/favourite-${index + 1}.jpg`}
                    alt=""
                    className="size-10 shrink-0 rounded-[10px] bg-[#e5e7eb] object-cover"
                  />
                  <div className="flex-1">
                    <p className="text-[14px] font-semibold leading-5 text-[#0a0a0a]">Favourite {name}</p>
                    <p className="text-[12px] leading-4 text-[#717182]">Your restaurant</p>
                  </div>
                  <span className="flex items-center gap-1 rounded-full bg-[#017bef]/[0.08] px-[12px] py-1.5 text-[12px] font-bold leading-4 text-[#017bef]">
                    <RotateCcw className="size-2.5" />
                    Reorder
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const TRACKING_STEPS = [
  { label: "Order Placed", state: "done" },
  { label: "Kitchen Confirmed", state: "done" },
  { label: "Preparing", state: "current" },
  { label: "Out for Delivery", state: "todo" },
  { label: "Delivered", state: "todo" },
];

export function EatTracking() {
  return (
    <section className="bg-white py-20">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-16 px-[16px] sm:px-8 lg:grid-cols-2">
        <div>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
            Order Tracking
          </p>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
            Know Where Your Order Is
          </h2>
          <p className="pt-[16px] text-[18px] leading-[29.25px] text-[#717182]">
            Real-time order tracking, from kitchen confirmation to delivery.
          </p>

          <ol className="pt-8">
            {TRACKING_STEPS.map((step, index) => {
              const reached = step.state !== "todo";
              return (
                <li key={step.label} className="relative flex items-start gap-[16px]">
                  {index < TRACKING_STEPS.length - 1 && (
                    <span
                      className={`absolute left-[19px] top-10 h-8 w-0.5 ${reached ? "bg-[#017bef]" : "bg-[#e5e7eb]"}`}
                    />
                  )}
                  <span
                    className={`flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-solid ${
                      reached ? "border-[#017bef] bg-[#017bef]" : "border-[#e5e7eb] bg-white"
                    }`}
                  >
                    {reached ? (
                      <Check className="size-3.5 text-white" />
                    ) : (
                      <span className="size-2 rounded-full bg-[#e5e7eb]" />
                    )}
                  </span>
                  {/* Row height comes from the text (pt 8 + pb 32), as in the design */}
                  <div className="pb-8 pt-2">
                    <p
                      className={`text-[14px] font-semibold leading-5 ${
                        step.state === "current"
                          ? "text-[#017bef]"
                          : step.state === "done"
                            ? "text-[#0a0a0a]"
                            : "text-[#717182]"
                      }`}
                    >
                      {step.label}
                    </p>
                    {step.state === "current" && (
                      <p className="pt-0.5 text-[12px] leading-4 text-[#717182]">In progress</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        <div>
          <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
            Live Rider Tracking
          </p>
          <h2 className="pt-[12px] text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
            Track Your Delivery in Real Time
          </h2>
          <p className="pt-[16px] text-[18px] leading-[29.25px] text-[#717182]">
            Live rider tracking with GPS and estimated arrival times.
          </p>

          <div className="mt-8 overflow-hidden rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-[#f5f8ff]">
            <div className="relative h-[224px] bg-gradient-to-br from-[#e8f4ff] via-[#f0f8ff] to-[#e5efff]">
              <img
                src="/figma-assets/eat/route.svg"
                alt=""
                className="absolute inset-0 size-full"
                style={{ objectFit: "fill" }}
              />
              {[
                {
                  left: "15.4%",
                  top: "64%",
                  label: "Restaurant",
                  dot: "R",
                  dotClass: "bg-[#017bef]",
                  text: "text-[#017bef]",
                },
                {
                  left: "50.7%",
                  top: "40%",
                  label: "Rider",
                  dot: "🏍",
                  dotClass: "bg-[#0a0a0a] border-2 border-solid border-white",
                  text: "text-[#0a0a0a]",
                },
                {
                  left: "83.5%",
                  top: "15%",
                  label: "Destination",
                  dot: "You",
                  dotClass: "bg-[#22c55e]",
                  text: "text-[#16a34a]",
                },
              ].map(pin => (
                <div
                  key={pin.label}
                  className="absolute flex -translate-x-1/2 flex-col items-center gap-1"
                  style={{ left: pin.left, top: pin.top }}
                >
                  <span
                    className={`flex size-8 items-center justify-center rounded-full text-[8px] font-bold leading-3 text-white shadow-md ${pin.dotClass}`}
                  >
                    {pin.dot}
                  </span>
                  <span
                    className={`rounded bg-white px-1.5 py-0.5 text-[9px] font-semibold leading-[13.5px] shadow-sm ${pin.text}`}
                  >
                    {pin.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t-[1px] border-solid border-[#e5e7eb] px-[20px] py-[16px]">
              <span className="flex items-center gap-2 text-[14px] font-semibold leading-5 text-[#0a0a0a]">
                <Clock className="size-3.5 text-[#017bef]" />
                Estimated Arrival
              </span>
              <span className="text-[14px] font-bold leading-5 text-[#017bef]">On the way</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
